import moment from "moment";
import { LoadState } from "../../../constants/enums";
import { getUniqueId } from "../../../helpers";
import { IHabitationalRiskLocation } from "../habitationalRiskLocation/habitationalRiskLocation.types";
import { IHabitationalInterested } from "../habitationalInterestedParty/habitationalInterested.types";
import { IHabitationalCoverages } from "../habitationalCoverages/habitationalCoverages.types";
import { IHabitationalScheduleDetail } from "../habitationalPropertySchedule/habitationalPropertySchedule.types";
import { STANDARD_APP_DATE_FORMAT } from "../../../constants/constants";
import { ITemplateField } from "../../CustomFormFields/CustomFormFields.types";

export interface IHabitationalPolicyChangeState {
  list: IHabitationalPolicyChange[];
  policyChangeData: IHabitationalPolicyChange;
  totalRecords: number;
  loading: LoadState;
  error: string | null;
}

export interface IHabitationalLossPayee {
  key: string;
  loc_no: number | null;
  name: string | null;
  interested_party_code: string | null;
  address: string;
  nature_of_interest: string;
}

export interface IHabitationalSchedulePersonal {
  key: string;
  action: string;
  hash: number;
  class_code: string;
  description: string;
  coverage_code: string;
  type_of_one: string;
  type_of_two: string;
  purchase: string;
  appraisal_date: string;
  deductible: string;
  disc_applicable: string;
  amount_of_insurance: string;
}
export interface IHabitationalWaterCraftTrailerOne {
  key: string;
  action: string;
  year: string;
  description_make_model: string;
  coverage: string;
  deductible: string;
  identification_serial_number: string;
}
export interface IHabitationalWaterCraftTrailerTwo {
  key: string;
  action: string;
  type: string;
  style: string;
  construction: string;
  length: string;
  horse_power: string;
  purchase_date: string;
  new_or_used: string;
  purchase_price: string;
  replacement_cost: string;
  value_contents_purchase_price: string;
  value_of_non_standard: string;
  maximum_speed: string;
}

export interface IHabitationalAttachment {
  key: string;
  attachments: string | null;
  remarks: string;
  yes_no: string;
  code: string;
  isTypeOther: boolean;
}

// V2 only: captures the "NEW" occupancy/protection values a user enters for
// a single risk location row in the Address Change panel. `selected` tracks
// the row checkbox; only checked risk locations are included when the
// request is submitted. Mirrors
// IAutomobileAddressChangeVehicleUpdate (automobilePolicyChangeReq.types.ts),
// adapted to Habitational's risk-location concept (no vehicles/drivers here).
export interface IHabitationalAddressChangeRiskLocationUpdate {
  risk_location_number: number | null;
  selected: boolean;
  occupancy_type: string | null;
  access_type: string | null;
  smokers: string | null;
  no_of_families: string | null;
}

// V2 only: "Location Assignment" — assigns an interested party
// (mortgagee/loss payee) to a risk location, with an optional nature-of-
// interest override for that location. Supports both Add (a brand new
// assignment) and Change (substituting an existing assignment for the same
// risk location/interested-party pairing), mirroring Automobile V2's
// IAutomobileDriverAssignment (vehicle/driver assignment) pattern, adapted to
// Habitational's risk-location/interested-party concept (no vehicles/drivers
// here).
export interface IHabitationalLocationAssignment {
  risk_location_number: number | null;
  interested_party_number: number | null;
  nature_of_interest: string | null;
  notes: string | null;
  // Per-record mode ("Add" | "Change"), captured at the time this record was
  // saved. Kept on the record itself (rather than derived from the shared
  // location_assignment_change.type) so adding a Change record later doesn't
  // retroactively relabel earlier Add records in "Changes Requested".
  mode?: "Add" | "Change" | null;
  // Substitution target for this specific record, only set when mode is
  // "Change".
  delete_location_assignment?: string | null;
}

export interface IHabitationalPolicyChange {
  incremental_number: number | null;
  customer_id: number | null;
  customer_policy_id: number | null;
  customer_name: string | null;
  customer_code: string | null;
  cancellation_reason: string | null;
  policy_type: string;
  branch_code: string | null;
  producer_one_code: string | null;
  producer_one_id: string | null;
  policy_status: string | null;
  policy_number: string | null;
  insurer: string | null;
  policy_effective_date: string | null;
  effective_time: string | null;
  expiry_date: string | null;
  type: string | null;
  sub_type: string | null;
  // date_change: string | null;
  // time_change: string | null;
  date_created: string | null;
  effective_date: string | null;
  days_left: string | null;
  due_date: string | null;
  date_completed: string | null;
  assigned_to_id: string ;
  assigned_to_name: string | null;
  current_policy_status: string | null;
  credit_card_info: {
    // Which kind of payment method the change is for. Drives which fields the
    // V2 form shows: "Bank" (PAD), "Credit Card", "Cheque", or "Other".
    payment_type: string | null;
    cc: number | null;
    expiry: string | null;
    cvv: number | null;
    account_holder_name: string | null;
    bank_name: string | null;
    institution_number: string | null;
    transit_number: string | null;
    account_number: string | null;
    // How the authorization was captured: "G4Sign" (sign via G4 Sign) or
    // "OnFile" (already on file).
    signature_type: string | null;
  };
  name_change: {
    new_name: string | null;
    date_of_birth: string | null;
    relationship: string | null;
  };
  address_change: {
    address_type: string | null;
    // V2 only: which parts of the address are changing — mirrors the
    // Automobile V2 "Mailing only / Garaging only / Mailing and garaging"
    // pill selector, adapted to Habitational's mailing/risk-location split.
    change_type:
      | "Mailing only"
      | "Risk location only"
      | "Mailing and risk location"
      | null;
    address: string | null;
    address_two: string | null;
    unit_number: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
    // V2 only: effective date and contact details captured on the Address
    // Change panel.
    effective_date: string | null;
    phone: string | null;
    email: string | null;
    // V2 only: per-risk-location re-rate fields shown in the Address Change
    // panel when the risk location address changes. One entry per risk
    // location the user checks in the list; `risk_location_number` ties back
    // to the risk location's `incremental_number`.
    risk_location_updates: IHabitationalAddressChangeRiskLocationUpdate[];
  };
  change_mailing_address_on_customer: string | null;
  risk_location_change: {
    type: string | null;
    delete_risk_location: string | null;
    // Snapshot of the replaced risk location (number + address), captured when
    // the record to replace is picked, so the "replaced" summary can show its
    // identifying details.
    delete_risk_location_details?: {
      incremental_number: string | null;
      address: string | null;
      city: string | null;
      province_or_state: string | null;
      postal_code: string | null;
    } | null;
    records: IHabitationalRiskLocation[];
  };
  interested_party_change: {
    type: string | null;
    delete_interested_party: string | null;
    // Snapshot of the replaced interested party (number + risk location + name).
    delete_interested_party_details?: {
      incremental_number: string | null;
      location_number: string | null;
      mortgage_or_loss_payee_name: string | null;
    } | null;
    records: IHabitationalInterested[]; //JSON
  };
  coverage_change: {
    type: string | null;
    delete_coverage: string | null;
    // Snapshot of the replaced coverage (number).
    delete_coverage_details?: {
      incremental_number: string | null;
    } | null;
    records: IHabitationalCoverages[]; //JSON
  };
  schedule_change: {
    type: string | null;
    delete_schedule: string | null;
    // Snapshot of the replaced schedule item (number + property number).
    delete_schedule_details?: {
      incremental_number: string | null;
      property_number: string | null;
    } | null;
    records: IHabitationalScheduleDetail[]; //JSON
  };
  // V2 only: see IHabitationalLocationAssignment above.
  location_assignment_change: {
    type: string | null;
    delete_location_assignment: string | null;
    records: IHabitationalLocationAssignment[];
  };

  attachments: {
    key: string;
    description: string | null;
    status: string | null;
    url: string | null;
    file: File | null;
  }[];

  underwriting: ITemplateField[][];
  remarks: string | null;
  change_request_code?: string;
  status: string;
  assigned_policy_number: string | null;
}

export const initialHabitationalPolicyChangeReqState: IHabitationalPolicyChangeState =
  {
    policyChangeData: {
      incremental_number: null,
      customer_id: null,
      customer_policy_id: null,
      customer_name: null,
      customer_code: null,
      cancellation_reason: null,
      policy_type: "",
      branch_code: null,
      producer_one_code: null,
      producer_one_id: null,
      policy_status: null,
      insurer: null,
      policy_number: null,
      policy_effective_date: null,
      effective_time: null,
      expiry_date: moment().hours(0).minutes(1).format(),
      type: "PolicyChange",
      sub_type: "Name Change",
      // date_change: moment().format(),
      // time_change: moment().format(),
      date_created: moment().format(STANDARD_APP_DATE_FORMAT),
       effective_date: moment().startOf('day').add(1, 'minute').format("YYYY-MM-DD HH:mm:ss"),
      // effective_date: moment()
      //   .startOf("day")
      //   .add(1, "minute")
      //   .format("YYYY-MM-DD HH:mm:ss"),
      days_left: null,
      due_date: moment().add(5, "days").format(STANDARD_APP_DATE_FORMAT),
      date_completed: null,
      assigned_to_id: "",
      credit_card_info: {
        payment_type: null,
        cc: null,
        expiry: null,
        cvv: null,
        account_holder_name: null,
        bank_name: null,
        institution_number: null,
        transit_number: null,
        account_number: null,
        signature_type: null,
      },
      name_change: {
        new_name: null,
        date_of_birth: null,
        relationship: null,
      },
      address_change: {
        address_type: "Mailing Address",
        change_type: null,
        address: null,
        address_two: null,
        unit_number: null,
        city: null,
        state: null,
        postal_code: null,
        country: null,
        effective_date: null,
        phone: null,
        email: null,
        risk_location_updates: [],
      },
      change_mailing_address_on_customer: null,
      risk_location_change: {
        type: null,
        delete_risk_location: null,
        delete_risk_location_details: null,
        records: [],
      },
      interested_party_change: {
        type: null,
        delete_interested_party: null,
        delete_interested_party_details: null,
        records: [],
      },
      coverage_change: {
        type: null,
        delete_coverage: null,
        delete_coverage_details: null,
        records: [],
      },
      schedule_change: {
        type: null,
        delete_schedule: null,
        delete_schedule_details: null,
        records: [],
      },
      location_assignment_change: {
        type: null,
        delete_location_assignment: null,
        records: [],
      },

      attachments: [
        {
          key: getUniqueId(),
          description: null,
          status: "Pending",
          url: null,
          file: null,
        },
      ],

      underwriting: [],
      remarks: null,
      status: "Active",
      current_policy_status: null,
      assigned_to_name: null,
      assigned_policy_number: null,
    },
    list: [],
    totalRecords: 0,
    loading: LoadState.NotLoaded,
    error: null,
  };
